/**
 * HydrationQuickLogModal - Standalone quick hydration logger
 * Simple, fast hydration logging without daily check-in complexity
 */

import React, { useState } from 'react';

const PRESET_AMOUNTS = [
  { label: 'Glass', ml: 250, icon: '🥛' },
  { label: 'Bottle', ml: 500, icon: '🍶' },
  { label: 'Large', ml: 750, icon: '🧴' },
  { label: '1 Liter', ml: 1000, icon: '💧' },
];

const HydrationQuickLogModal = ({
  isOpen,
  onClose,
  userId,
  currentMl = 0,
  goalMl = 2500,
  onSuccess,
  BACKEND_URL
}) => {
  const [logging, setLogging] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [showSubtract, setShowSubtract] = useState(false);
  
  if (!isOpen) return null;
  
  const currentLiters = (currentMl / 1000).toFixed(1);
  const goalLiters = (goalMl / 1000).toFixed(1);
  const percent = Math.min(100, Math.round((currentMl / goalMl) * 100));
  const remaining = Math.max(0, goalMl - currentMl);
  const remainingLiters = (remaining / 1000).toFixed(1);
  
  const logHydration = async (amountMl) => {
    if (logging || amountMl <= 0) return;
    
    setLogging(true);
    try {
      const tzOffset = Math.round(-new Date().getTimezoneOffset() / 60);
      
      const response = await fetch(`${BACKEND_URL}/api/hydration/quick-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          amount_ml: amountMl,
          tz_offset: tzOffset
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (onSuccess) {
          onSuccess(data);
        }
        onClose();
      } else {
        alert('Failed to log hydration');
      }
    } catch (error) {
      console.error('Error logging hydration:', error);
      alert('Failed to log hydration');
    } finally {
      setLogging(false);
    }
  };
  
  const subtractHydration = async (amountMl) => {
    if (logging || amountMl <= 0) return;
    
    setLogging(true);
    try {
      const tzOffset = Math.round(-new Date().getTimezoneOffset() / 60);
      
      const response = await fetch(`${BACKEND_URL}/api/hydration/subtract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          amount_ml: amountMl,
          tz_offset: tzOffset
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (onSuccess) {
          onSuccess(data);
        }
        setShowSubtract(false);
        onClose();
      } else {
        alert('Failed to subtract hydration');
      }
    } catch (error) {
      console.error('Error subtracting hydration:', error);
      alert('Failed to subtract hydration');
    } finally {
      setLogging(false);
    }
  };
  
  const resetHydration = async () => {
    if (logging) return;
    if (!window.confirm('Reset today\'s hydration to 0?')) return;
    
    setLogging(true);
    try {
      const tzOffset = Math.round(-new Date().getTimezoneOffset() / 60);
      
      const response = await fetch(`${BACKEND_URL}/api/hydration/reset/${userId}?tz_offset=${tzOffset}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        if (onSuccess) {
          onSuccess({ total_ml: 0, total_liters: 0, percent: 0, goal_met: false });
        }
        setShowSubtract(false);
        onClose();
      } else {
        alert('Failed to reset hydration');
      }
    } catch (error) {
      console.error('Error resetting hydration:', error);
      alert('Failed to reset hydration');
    } finally {
      setLogging(false);
    }
  };
  
  const handleCustomSubmit = () => {
    const amount = parseInt(customAmount);
    if (amount > 0 && amount <= 5000) {
      if (showSubtract) {
        subtractHydration(amount);
      } else {
        logHydration(amount);
      }
    }
  };
  
  return (
    <div 
      className="modal-overlay" 
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card, #1a1a2e)',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '360px',
          width: '100%',
          border: '1px solid rgba(56, 189, 248, 0.2)'
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '40px', marginBottom: '8px' }}>💧</div>
          <h2 style={{ 
            margin: 0, 
            fontSize: '20px', 
            fontWeight: '700',
            color: 'var(--text-primary)'
          }}>
            Log Hydration
          </h2>
          <p style={{ 
            margin: '8px 0 0 0', 
            fontSize: '13px', 
            color: 'var(--text-secondary)'
          }}>
            Quick add water intake
          </p>
        </div>
        
        {/* Current Progress */}
        <div style={{
          background: 'rgba(56, 189, 248, 0.1)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <div style={{ 
            fontSize: '28px', 
            fontWeight: '700', 
            color: '#38bdf8',
            marginBottom: '4px'
          }}>
            {currentLiters} L
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: 'var(--text-secondary)',
            marginBottom: '10px'
          }}>
            of {goalLiters} L goal ({percent}%)
          </div>
          
          {/* Progress bar */}
          <div style={{ 
            width: '100%', 
            height: '8px', 
            background: 'rgba(255,255,255,0.1)', 
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{ 
              width: `${percent}%`, 
              height: '100%', 
              background: percent >= 100 
                ? 'linear-gradient(90deg, #38bdf8, #38ef7d)'
                : '#38bdf8',
              borderRadius: '4px',
              transition: 'width 0.3s ease'
            }} />
          </div>
          
          {remaining > 0 && (
            <div style={{ 
              marginTop: '8px', 
              fontSize: '11px', 
              color: 'var(--text-muted)'
            }}>
              {remainingLiters} L to reach goal
            </div>
          )}
        </div>
        
        {/* Mode Toggle - Add vs Subtract */}
        {currentMl > 0 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '8px',
            marginBottom: '16px'
          }}>
            <button
              onClick={() => setShowSubtract(false)}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '8px',
                background: !showSubtract ? 'rgba(56, 189, 248, 0.2)' : 'transparent',
                color: !showSubtract ? '#38bdf8' : 'var(--text-secondary)',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ➕ Add
            </button>
            <button
              onClick={() => setShowSubtract(true)}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '8px',
                background: showSubtract ? 'rgba(239, 68, 68, 0.2)' : 'transparent',
                color: showSubtract ? '#ef4444' : 'var(--text-secondary)',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ➖ Edit
            </button>
          </div>
        )}
        
        {/* Preset Buttons - Add Mode */}
        {!showSubtract && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '10px',
            marginBottom: '16px'
          }}>
            {PRESET_AMOUNTS.map((preset) => (
              <button
                key={preset.ml}
                onClick={() => logHydration(preset.ml)}
                disabled={logging}
                style={{
                  padding: '14px 12px',
                  border: 'none',
                  borderRadius: '12px',
                  background: 'rgba(56, 189, 248, 0.15)',
                  color: '#38bdf8',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: logging ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  opacity: logging ? 0.6 : 1
                }}
              >
                <span style={{ fontSize: '20px' }}>{preset.icon}</span>
                <span>{preset.label}</span>
                <span style={{ fontSize: '11px', opacity: 0.7 }}>+{preset.ml}ml</span>
              </button>
            ))}
          </div>
        )}
        
        {/* Subtract Mode */}
        {showSubtract && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '10px',
              marginBottom: '12px'
            }}>
              {PRESET_AMOUNTS.map((preset) => (
                <button
                  key={preset.ml}
                  onClick={() => subtractHydration(preset.ml)}
                  disabled={logging || currentMl < preset.ml}
                  style={{
                    padding: '14px 12px',
                    border: 'none',
                    borderRadius: '12px',
                    background: 'rgba(239, 68, 68, 0.15)',
                    color: '#ef4444',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: (logging || currentMl < preset.ml) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    opacity: (logging || currentMl < preset.ml) ? 0.4 : 1
                  }}
                >
                  <span style={{ fontSize: '20px' }}>{preset.icon}</span>
                  <span>{preset.label}</span>
                  <span style={{ fontSize: '11px', opacity: 0.7 }}>-{preset.ml}ml</span>
                </button>
              ))}
            </div>
            
            {/* Reset Button */}
            <button
              onClick={resetHydration}
              disabled={logging || currentMl === 0}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '10px',
                background: 'transparent',
                color: '#ef4444',
                fontSize: '13px',
                fontWeight: '600',
                cursor: (logging || currentMl === 0) ? 'not-allowed' : 'pointer',
                opacity: (logging || currentMl === 0) ? 0.4 : 1
              }}
            >
              🗑️ Reset to 0
            </button>
          </div>
        )}
        
        {/* Custom Amount */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            gap: '8px',
            alignItems: 'center'
          }}>
            <input
              type="number"
              placeholder={showSubtract ? "Amount to subtract (ml)" : "Custom ml"}
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              style={{
                flex: 1,
                padding: '12px',
                border: `1px solid ${showSubtract ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '10px',
                background: 'rgba(255,255,255,0.05)',
                color: 'var(--text-primary)',
                fontSize: '14px'
              }}
            />
            <button
              onClick={handleCustomSubmit}
              disabled={logging || !customAmount || parseInt(customAmount) <= 0}
              style={{
                padding: '12px 20px',
                border: 'none',
                borderRadius: '10px',
                background: showSubtract ? '#ef4444' : '#38bdf8',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                opacity: (logging || !customAmount) ? 0.5 : 1
              }}
            >
              {showSubtract ? 'Remove' : 'Add'}
            </button>
          </div>
        </div>
        
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '14px',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px',
            background: 'transparent',
            color: 'var(--text-secondary)',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default HydrationQuickLogModal;
